/**
 * Search (t189u): one box over everything you can open - people, announcements, tasks, issues, channels, buildings,
 * Help Centre guides and knowledge base articles, and messages. The API applies each kind's own visibility rule; the
 * guides are searched in the browser. Every result opens its item; people always open the person sheet.
 *
 * t303u: Sort (Relevance, Newest, Oldest), the shared date range (Any time by default), From (a person) and In (a
 * conversation you're in), all in the URL (?type= ?sort= ?range= ?from= ?in=) so they survive refresh and Back. They
 * apply to the dated kinds (messages, tasks, announcements, issues, articles); on an undated tab (People, Channels,
 * Buildings, Events, Help) they are hidden and not sent. Each section is a short list with Show more (next 20). The
 * type is a plain tab row on both layouts; on the phone the filters are in a Filters sheet.
 */
import clsx from 'clsx';
import { DateTime } from 'luxon';
import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { PersonButton } from '@/features/people/PersonDrawer';
import { RoomIcon } from '@/features/rooms/RoomIcon';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useSearch, useSearchMore, type SearchFilters, type SearchType } from '@tradeoutlet/data';
import { Building2, ChevronRight, CalendarDays, CheckSquare, CircleHelp, Clock3, Hash, Lock, Megaphone, MessageSquare, Search as SearchIcon, ShieldCheck, Siren, Users, X } from 'lucide-react';
import { isModuleOn, moduleForSearchType, pathAvailable, SEARCH_QUICK_JUMPS, SEARCH_SORTS, parseRecentSearches, pushRecentSearch, recentSearchesKey, type SearchKind, type SearchResult, type SearchSort } from '@tradeoutlet/shared';
import { messageTimestamp, DATE_FORMATS, dt } from '@/lib/format';
import { conversationName, useConversations, useMembers, useWorkspaceId } from '@/lib/hooks';
import { useUi } from '@/lib/store';
import { useModulesOff } from '@/lib/modules';
import { Avatar, EmptyState, ErrorBox, FilterBar, FilterSheetContext, Label, PageHeader, Select, Spinner, Tabs } from '@/components/ui';
import { DateRangeFilter, useDateRangeParam } from '@/components/ui/DateRangeFilter';
import { ALL_PRESETS, londonBounds, type RangePreset } from '@/lib/dateRange';
import { BottomSheet } from '@/mobile/BottomSheet';
import { primaryBtn } from '@/mobile/FeedCards';
import { useHelpSearch } from '@/features/help/HelpSearch';
import { helpHref } from '@/features/help/data';
import { useStartDm } from './useStartDm';
import { useMobileLayout } from '@/mobile/layout';
import { useAuth } from '@/lib/auth';

type Tab = SearchType | 'help';

/** Renders a Postgres ts_headline (<mark>..</mark> markers) safely without innerHTML. */
function Headline({ text }: { text: string }) {
  const { byId } = useMembers();
  const clean = text.replace(/<@([0-9a-f-]{36})>/g, (_, id: string) => `@${byId.get(id)?.display_name ?? 'unknown'}`).replace(/[«»]/g, m => (m === '«' ? '<mark>' : '</mark>'));
  const parts = clean.split(/<mark>|<\/mark>/);
  return <span>{parts.map((p, i) => (i % 2 ? <mark key={i} className="rounded bg-orange-100 px-0.5 text-inherit dark:bg-orange-800/60">{p}</mark> : <span key={i}>{p}</span>))}</span>;
}
/** The help index marks matches in escaped HTML; show it as text with the same marks. */
const unescape = (t: string) => t.replace(/&amp;/g, '&').replace(/&lt;/g, '<').replace(/&gt;/g, '>').replace(/&quot;/g, '"').replace(/&#39;/g, "'");

function Section({ icon, title, children, testId }: { icon: ReactNode; title: string; children: ReactNode; testId: string }) {
  return (
    <section data-testid={testId}>
      <h2 className="mb-2 flex items-center gap-2 text-sm font-semibold text-slate-500">{icon}{title}</h2>
      <div className="divide-y divide-slate-100 overflow-hidden rounded-lg border border-slate-200 bg-white dark:divide-slate-800 dark:border-slate-800 dark:bg-slate-900">{children}</div>
    </section>
  );
}
/** A result row. A div with the button role, so a name inside it (PersonButton) can open the person sheet. */
/** t645u: a public channel you're not in opens as a preview with Join. */
export const channelSub = (c: { is_member: boolean; description: string | null }) => [c.is_member ? null : 'Not a member · open to preview and join', c.description].filter(Boolean).join(' · ') || undefined;

function Row({ onClick, icon, title, sub, testId }: { onClick: () => void; icon?: ReactNode; title: ReactNode; sub?: ReactNode; testId: string }) {
  return (
    <div role="button" tabIndex={0} onClick={onClick} onKeyDown={e => { if ((e.key === 'Enter' || e.key === ' ') && e.target === e.currentTarget) { e.preventDefault(); onClick(); } }} data-testid={testId}
      className="flex min-h-[48px] w-full cursor-pointer items-center gap-3 px-3 py-2 text-left hover:bg-slate-50 focus-visible:outline-2 focus-visible:-outline-offset-2 active:bg-slate-100 dark:hover:bg-slate-800">
      {icon && <span className="shrink-0 text-slate-500">{icon}</span>}
      <span className="min-w-0 flex-1"><span className="block truncate font-medium">{title}</span>{sub && <span className="block truncate text-xs text-slate-500">{sub}</span>}</span>
    </div>
  );
}

/** t303u: the date presets offered (Any time first and the default). */
export const SEARCH_RANGE_PRESETS: RangePreset[] = ['any', ...ALL_PRESETS];
const SORT_LABELS: Record<SearchSort, string> = { relevance: 'Relevance', newest: 'Newest', oldest: 'Oldest' };
/** Tabs the filters apply to (the dated kinds). */
const FILTERED_TABS: readonly Tab[] = ['all', 'messages', 'tasks', 'announcements', 'issues'];
const TAB_IDS: readonly Tab[] = ['all', 'people', 'announcements', 'tasks', 'issues', 'channels', 'buildings', 'events', 'help', 'messages'];
const mutedLink = 'text-sm font-medium text-slate-600 hover:text-slate-900 hover:underline dark:text-slate-400 dark:hover:text-white';

/** Which sections have Show more open, per search (so Back to the results reopens them from the cache). */
const openedKey = (search: string) => `collab.search.more:${search}`;
function readOpened(search: string): SearchKind[] { try { return JSON.parse(sessionStorage.getItem(openedKey(search)) ?? '[]') as SearchKind[]; } catch { return []; } }
function writeOpened(search: string, kinds: SearchKind[]) { try { sessionStorage.setItem(openedKey(search), JSON.stringify(kinds)); } catch { /* private mode */ } }

interface MoreCtx { ws: string | null | undefined; q: string; filters: SearchFilters; opened: SearchKind[]; open: (k: SearchKind) => void }

/**
 * One section's rows: the first page from the main query, then "Show more" pages of 20 for that kind. The button is
 * plain muted text under the list; it goes when there is nothing more.
 */
function More<T extends { id?: string; user_id?: string }>({ kind, first, more, ctx, children, inset = true }: { kind: SearchKind; first: T[]; more: boolean; ctx: MoreCtx; children: (rows: T[]) => ReactNode; inset?: boolean }) {
  const on = ctx.opened.includes(kind);
  const inf = useSearchMore(ctx.ws, ctx.q, kind, ctx.filters, first.length, on && more);
  const rows = useMemo(() => {
    const seen = new Set<string>();
    const all = [...first, ...((inf.data?.pages ?? []).flatMap(p => (p[kind] ?? []) as unknown as T[]))];
    return all.filter(r => { const k = r.id ?? r.user_id ?? ''; if (seen.has(k)) return false; seen.add(k); return true; });
  }, [first, inf.data, kind]);
  const hasMore = !more ? false : !on ? true : inf.isLoading || inf.hasNextPage;
  const busy = inf.isFetching;
  return (
    <>
      {children(rows)}
      {hasMore && (
        <div className={inset ? 'px-3 py-2' : 'pt-2'}>
          <button type="button" disabled={busy} onClick={() => (on ? void inf.fetchNextPage() : ctx.open(kind))} className={clsx(mutedLink, 'disabled:opacity-60')} data-testid={`search-more-${kind}`}>
            {busy ? 'Loading…' : 'Show more'}
          </button>
        </div>
      )}
    </>
  );
}

/** t303u: the filter values in the URL, and setters that replace the entry (Back still goes to where you came from). */
function useSearchFilters() {
  const [params, setParams] = useSearchParams();
  const [dates, setDates, defaultDates] = useDateRangeParam({ fallback: 'any', presets: SEARCH_RANGE_PRESETS });
  const rawSort = params.get('sort');
  const sort: SearchSort = (SEARCH_SORTS as readonly string[]).includes(rawSort ?? '') ? rawSort as SearchSort : 'relevance';
  const from = params.get('from') ?? '';
  const inId = params.get('in') ?? '';
  const rawType = params.get('type') ?? 'all';
  const tab: Tab = (TAB_IDS as readonly string[]).includes(rawType) ? rawType as Tab : 'all';
  const set = useCallback((k: string, v: string, fallback = '') => setParams(prev => {
    const next = new URLSearchParams(prev);
    if (!v || v === fallback) next.delete(k); else next.set(k, v);
    return next;
  }, { replace: true }), [setParams]);
  const clear = useCallback(() => setParams(prev => {
    const next = new URLSearchParams(prev);
    for (const k of ['sort', 'range', 'from', 'in']) next.delete(k);
    return next;
  }, { replace: true }), [setParams]);
  const active = (sort !== 'relevance' ? 1 : 0) + (defaultDates ? 0 : 1) + (from ? 1 : 0) + (inId ? 1 : 0);
  return {
    tab, setTab: (t: Tab) => set('type', t, 'all'), sort, setSort: (v: SearchSort) => set('sort', v, 'relevance'), dates, setDates, defaultDates,
    from, setFrom: (v: string) => set('from', v), inId, setIn: (v: string) => set('in', v), clear, active,
  };
}

/** The Sort, Dates, From and In controls (a row on desktop; stacked with labels in the phone sheet). */
function FilterControls({ f, stacked, onDatePicked }: { f: ReturnType<typeof useSearchFilters>; stacked?: boolean; onDatePicked?: () => void }) {
  const { members } = useMembers();
  const { userId } = useAuth();
  const convs = useConversations().data?.conversations;
  const people = useMemo(() => members.filter(m => m.status === 'active').sort((a, b) => a.display_name.localeCompare(b.display_name, 'en-GB')), [members]);
  // In: conversations you're a member of (channels first, then DMs, group chats and ad-hoc meeting rooms), never entity threads.
  const places = useMemo(() => (convs ?? []).filter(c => c.is_member && c.kind !== 'entity' && !c.archived_at)
    .map(c => ({ id: c.id, channel: c.kind === 'channel', name: c.kind === 'channel' ? `#${c.slug ?? c.title ?? 'channel'}` : conversationName(c, userId) }))
    .sort((a, b) => (a.channel === b.channel ? a.name.localeCompare(b.name, 'en-GB') : a.channel ? -1 : 1)), [convs, userId]);
  const field = (label: string, id: string, control: ReactNode) => (stacked ? <div><Label htmlFor={id}>{label}</Label>{control}</div> : control);
  return (
    <>
      {field('Sort', 'search-sort', <Select id="search-sort" aria-label="Sort" value={f.sort} onChange={e => f.setSort(e.target.value as SearchSort)} data-testid="search-sort">
        {SEARCH_SORTS.map(s => <option key={s} value={s}>{SORT_LABELS[s]}</option>)}
      </Select>)}
      <DateRangeFilter value={f.dates} presets={SEARCH_RANGE_PRESETS} onChange={v => { f.setDates(v); if (v.preset !== 'custom') onDatePicked?.(); }} label="Dates" data-testid="search-dates" />
      {field('From', 'search-from', <Select id="search-from" aria-label="From" value={f.from} onChange={e => f.setFrom(e.target.value)} data-testid="search-from">
        <option value="">From anyone</option>
        {people.map(m => <option key={m.user_id} value={m.user_id}>{m.display_name}</option>)}
      </Select>)}
      {field('In', 'search-in', <Select id="search-in" aria-label="In" value={f.inId} onChange={e => f.setIn(e.target.value)} data-testid="search-in">
        <option value="">In any chat</option>
        {places.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
      </Select>)}
    </>
  );
}


/** How many results each kind has (help counted in the browser). */
export function searchCounts(d: SearchResult | undefined, help: number): Partial<Record<Tab, number>> {
  if (!d) return {};
  return { people: d.people.length, announcements: d.announcements.length, tasks: d.tasks.length, issues: d.issues.length, channels: d.channels.length,
    buildings: d.buildings.length, articles: d.articles.length, policies: d.policies?.length ?? 0, help, messages: d.messages.length, events: d.events?.length ?? 0 };
}

/** t344u: your recent searches on this browser (per workspace). */
function useRecentSearches(ws: string | null | undefined) {
  const key = ws ? recentSearchesKey(ws) : null;
  const read = useCallback(() => { try { return key ? parseRecentSearches(localStorage.getItem(key)) : []; } catch { return []; } }, [key]);
  const [list, setList] = useState<string[]>(read);
  useEffect(() => setList(read()), [read]);
  const save = useCallback((next: string[]) => { setList(next); try { if (key) localStorage.setItem(key, JSON.stringify(next)); } catch { /* private mode */ } }, [key]);
  return { list, add: (q: string) => save(pushRecentSearch(read(), q)), remove: (q: string) => save(read().filter(x => x !== q)), clear: () => save([]) };
}

/** t344u: before you type - recent searches and quick jumps, instead of "Type at least two characters". */
function SearchStart({ recents, onPick, onGo }: { recents: ReturnType<typeof useRecentSearches>; onPick: (q: string) => void; onGo: (path: string) => void }) {
  const off = useModulesOff();
  return (
    <div className="mx-auto w-full max-w-5xl space-y-6" data-testid="search-start">
      {recents.list.length > 0 && (
        <section data-testid="search-recent">
          <div className="mb-2 flex items-center justify-between">
            <h2 className="text-sm font-semibold text-slate-500">Recent searches</h2>
            <button type="button" onClick={recents.clear} className="text-sm text-slate-500 hover:text-slate-900 hover:underline dark:hover:text-slate-100" data-testid="search-recent-clear">Clear</button>
          </div>
          <div className="divide-y divide-slate-100 overflow-hidden rounded-lg border border-slate-200 bg-white dark:divide-slate-800 dark:border-slate-800 dark:bg-slate-900">
            {recents.list.map(r => (
              <div key={r} className="flex items-center">
                <button type="button" onClick={() => onPick(r)} className="flex min-h-[48px] min-w-0 flex-1 items-center gap-3 px-3 py-2 text-left text-sm hover:bg-slate-50 dark:hover:bg-slate-800" data-testid="search-recent-item">
                  <Clock3 className="h-4 w-4 shrink-0 text-slate-400" /><span className="truncate">{r}</span>
                </button>
                <button type="button" onClick={() => recents.remove(r)} aria-label={`Remove ${r} from recent searches`} className="mr-1 flex h-11 w-11 shrink-0 items-center justify-center text-slate-400 hover:text-slate-700"><X className="h-4 w-4" /></button>
              </div>
            ))}
          </div>
        </section>
      )}
      <section data-testid="search-jumps">
        <h2 className="mb-2 text-sm font-semibold text-slate-500">Go to</h2>
        <div className="grid gap-px overflow-hidden rounded-lg border border-slate-200 bg-slate-200 sm:grid-cols-2 lg:grid-cols-3 dark:border-slate-800 dark:bg-slate-800">
          {SEARCH_QUICK_JUMPS.filter(j => pathAvailable(off, j.web)).map(j => (
            <button key={j.id} type="button" onClick={() => onGo(j.web)} className="flex min-h-[48px] items-center justify-between gap-3 bg-white px-3 py-2 text-left text-sm font-medium hover:bg-slate-50 dark:bg-slate-900 dark:hover:bg-slate-800" data-testid={`search-jump-${j.id}`}>
              {j.label}<ChevronRight className="h-4 w-4 text-slate-400" />
            </button>
          ))}
        </div>
      </section>
    </div>
  );
}

export function SearchPage() {
  const [params, setParams] = useSearchParams();
  const mobile = useMobileLayout();
  const openPerson = useUi(s => s.openPerson);
  const q = params.get('q') ?? '';
  const ws = useWorkspaceId();
  const nav = useNavigate();
  const { userId } = useAuth();
  const { byId } = useMembers();
  const openTask = useUi(s => s.openTask);
  const startDm = useStartDm();
  const f = useSearchFilters();
  const { tab, setTab } = f;
  const [sheet, setSheet] = useState(false);
  // Filters only go to the API on the tabs they apply to.
  const filtered = FILTERED_TABS.includes(tab);
  const filters = useMemo<SearchFilters>(() => {
    if (!filtered) return {};
    const b = londonBounds(f.dates);
    return { sort: f.sort === 'relevance' ? undefined : f.sort, after: b?.start, before: b?.end, from: f.from || undefined, in: f.inId || undefined };
  }, [filtered, f.dates, f.sort, f.from, f.inId]);
  const narrowed = Boolean(filters.after || filters.from || filters.in);
  const res = useSearch(ws, q, tab === 'help' ? 'all' : tab, filters);
  const helpHits = useHelpSearch(q.trim().length >= 2 ? q : '', 8).hits;
  // A date, person or chat filter leaves out what it can't apply to, the Help Centre guides included.
  const help = narrowed ? [] : helpHits;
  const d = res.data;
  const show = (t: Tab) => tab === 'all' || tab === t;
  const n = searchCounts(d, help.length);
  const total = Object.values(n).reduce((a, b) => a + (b ?? 0), 0);
  // t369u: no tab for a switched-off module (the API leaves its results out too).
  const off = useModulesOff();
  const tabs: { id: Tab; label: string }[] = ([
    { id: 'all', label: 'All' }, { id: 'people', label: 'People' }, { id: 'announcements', label: 'Announcements' }, { id: 'tasks', label: 'Tasks' },
    { id: 'issues', label: 'Issues' }, { id: 'channels', label: 'Channels' }, { id: 'buildings', label: 'Buildings' }, { id: 'events', label: 'Events' },
    { id: 'policies', label: 'Policies' }, { id: 'help', label: 'Help' }, { id: 'messages', label: 'Messages' },
  ] as { id: Tab; label: string }[]).filter(t => isModuleOn(off, moduleForSearchType(t.id)));
  const search = params.toString();
  const [opened, setOpened] = useState<{ search: string; kinds: SearchKind[] }>(() => ({ search, kinds: readOpened(search) }));
  const openKinds = opened.search === search ? opened.kinds : readOpened(search);
  const ctx: MoreCtx = { ws, q, filters, opened: openKinds, open: k => { const kinds = [...new Set([...openKinds, k])]; writeOpened(search, kinds); setOpened({ search, kinds }); } };
  const more = (k: SearchKind) => Boolean(d?.more?.[k]);
  const when = (iso?: string | null) => (iso ? dt(iso).toFormat(dt(iso).year === DateTime.now().year ? DATE_FORMATS.day : DATE_FORMATS.date) : null);
  const person = (id: string | null | undefined, name?: string | null) => (id ? <PersonButton userId={id} name={name ?? byId.get(id)?.display_name}
    className="rounded font-medium text-slate-600 hover:underline dark:text-slate-300">{name ?? byId.get(id)?.display_name ?? 'Someone'}</PersonButton> : null);
  const recents = useRecentSearches(ws);
  // A search counts as recent once it has settled for a moment and come back.
  const settled = q.trim().length >= 2 && Boolean(d);
  useEffect(() => { if (!settled) return; const t = setTimeout(() => recents.add(q), 1200); return () => clearTimeout(t); }, [settled, q]); // eslint-disable-line react-hooks/exhaustive-deps
  const pick = (v: string) => setParams(prev => { const next = new URLSearchParams(prev); next.set('q', v); return next; });
  const join = (...parts: ReactNode[]) => parts.filter(Boolean).flatMap((p, i) => (i ? [<span key={`s${i}`}> · </span>, <span key={i}>{p}</span>] : [<span key={i}>{p}</span>]));

  return (
    <div className="flex h-full flex-col">
      {mobile ? (
        // Phase 0 phone search: the field is on the page (no top search box), keyboard up on open.
        <form className="shrink-0 px-3 py-2" role="search" onSubmit={e => e.preventDefault()}>
          <label className="flex min-h-[44px] items-center gap-2 rounded-xl bg-slate-100 px-3 text-slate-500 focus-within:ring-2 focus-within:ring-black dark:bg-slate-800 dark:focus-within:ring-white">
            <SearchIcon className="h-5 w-5" />
            <input type="search" autoFocus value={q} onChange={e => setParams(prev => { const next = new URLSearchParams(prev); if (e.target.value) next.set('q', e.target.value); else next.delete('q'); return next; }, { replace: true })} aria-label="Search" placeholder="Search" data-testid="mobile-search-input"
              className="w-full bg-transparent text-base text-slate-900 outline-none placeholder:text-slate-500 dark:text-white [&::-webkit-search-cancel-button]:hidden" />
            {q && <button type="button" aria-label="Clear" onClick={() => setParams(prev => { const next = new URLSearchParams(prev); next.delete('q'); return next; }, { replace: true })} className="-mr-2 flex h-11 w-11 items-center justify-center"><X className="h-4 w-4" /></button>}
          </label>
        </form>
      ) : <PageHeader icon={<SearchIcon className="h-5 w-5" />} title={q ? `Results for “${q}”` : 'Search'} />}
      {mobile ? (
        // t303u: a plain tab row (no chips), and Filters on the right for the dated kinds.
        <div className="flex shrink-0 items-center border-b border-slate-200 dark:border-slate-800" data-testid="search-scopes">
          <Tabs className="min-w-0 flex-1 px-1" value={tab} onChange={setTab} tabs={tabs} label="Search in" />
          {filtered && <button type="button" onClick={() => setSheet(true)} aria-haspopup="dialog" className={clsx(mutedLink, 'shrink-0 px-3 py-2')} data-testid="search-filters-open">
            {f.active ? `Filters (${f.active})` : 'Filters'}</button>}
        </div>
      ) : <Tabs className="px-3 sm:px-6" value={tab} onChange={setTab} tabs={tabs} label="Search in" />}
      {mobile && (
        <BottomSheet open={sheet} onClose={() => setSheet(false)} title="Filters" testId="search-filters-sheet"
          footer={<div className="flex gap-2 px-4 pb-2">
            {f.active > 0 && <button type="button" onClick={f.clear} className={clsx(mutedLink, 'px-3')} data-testid="search-filters-clear">Clear</button>}
            <button type="button" className={clsx(primaryBtn, 'flex-1')} onClick={() => setSheet(false)} data-testid="search-filters-done">Done</button>
          </div>}>
          <FilterSheetContext.Provider value={true}>
            <div className="flex flex-col gap-4 px-4 pb-4 [&_select]:w-full"><FilterControls f={f} stacked /></div>
          </FilterSheetContext.Provider>
        </BottomSheet>
      )}
      <div className={`scroll-thin flex-1 overflow-y-auto ${mobile ? 'p-3' : 'p-6'}`}>
        {!mobile && filtered && (
          <div className="mx-auto mb-4 w-full max-w-5xl" data-testid="search-filters">
            <FilterBar active={f.active} onClear={f.clear}><FilterControls f={f} /></FilterBar>
          </div>
        )}
        {q.trim().length < 2 && <SearchStart recents={recents} onPick={pick} onGo={nav} />}
        {res.isLoading && <div className="flex justify-center p-8"><Spinner /></div>}
        <ErrorBox error={res.error} />
        {d && !total && <EmptyState title="No results">Try different words.</EmptyState>}
        <div className="mx-auto w-full max-w-5xl space-y-6">
          {d && d.people.length > 0 && show('people') && (
            <Section icon={<Users className="h-4 w-4" />} title="People" testId="search-people">
              <More kind="people" first={d.people} more={more('people')} ctx={ctx}>{rows => rows.map(p => (
                <div key={p.user_id} className="flex items-center">
                  <Row testId="search-person" onClick={() => openPerson(p.user_id)} icon={<Avatar size={36} name={p.display_name} id={p.user_id} url={byId.get(p.user_id)?.avatar_url} />}
                    title={p.display_name} sub={[p.job_title, p.location_name].filter(Boolean).join(' · ') || p.email} />
                  {!mobile && p.user_id !== userId && <button type="button" aria-label={`Message ${p.display_name}`} title="Message" onClick={() => void startDm([p.user_id])}
                    className="mr-2 flex h-9 w-9 shrink-0 items-center justify-center rounded-md text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800"><MessageSquare className="h-4 w-4" /></button>}
                </div>
              ))}</More>
            </Section>
          )}
          {d && d.announcements.length > 0 && show('announcements') && (
            <Section icon={<Megaphone className="h-4 w-4" />} title="Announcements" testId="search-announcements">
              <More kind="announcements" first={d.announcements} more={more('announcements')} ctx={ctx}>{rows => rows.map(a => <Row key={a.id} testId="search-announcement" onClick={() => nav(`/announcements/${a.id}`)} title={a.title}
                sub={join(person(a.author_id, a.author_name), dt(a.publish_at).toFormat(DATE_FORMATS.day))} />)}</More>
            </Section>
          )}
          {d && d.tasks.length > 0 && show('tasks') && (
            <Section icon={<CheckSquare className="h-4 w-4" />} title="Tasks" testId="search-tasks">
              <More kind="tasks" first={d.tasks} more={more('tasks')} ctx={ctx}>{rows => rows.map(t => <Row key={t.id} testId="search-task" onClick={() => openTask(t.id)} title={t.title}
                sub={join(t.no_project ? null : t.project_name, t.headline ? <Headline text={t.headline} /> : null, f.sort !== 'relevance' ? when(t.created_at) : null)} />)}</More>
            </Section>
          )}
          {d && d.issues.length > 0 && show('issues') && (
            <Section icon={<Siren className="h-4 w-4" />} title="Issues" testId="search-issues">
              <More kind="issues" first={d.issues} more={more('issues')} ctx={ctx}>{rows => rows.map(i => <Row key={i.id} testId="search-issue" onClick={() => nav(`/incidents/${i.id}`)} title={`#${i.ref} ${i.title}`}
                sub={join(i.location_name, i.reporter_id ? person(i.reporter_id) : null, when(i.created_at))} />)}</More>
            </Section>
          )}
          {d && d.channels.length > 0 && show('channels') && (
            <Section icon={<Hash className="h-4 w-4" />} title="Channels" testId="search-channels">
              <More kind="channels" first={d.channels} more={more('channels')} ctx={ctx}>{rows => rows.map(c => <Row key={c.id} testId="search-channel" onClick={() => nav(`/c/${c.id}`)} icon={c.is_private ? <Lock className="h-4 w-4" /> : <Hash className="h-4 w-4" />}
                title={c.slug ?? c.title ?? 'Channel'} sub={channelSub(c)} />)}</More>
            </Section>
          )}
          {d && d.buildings.length > 0 && show('buildings') && (
            <Section icon={<Building2 className="h-4 w-4" />} title="Buildings" testId="search-buildings">
              <More kind="buildings" first={d.buildings} more={more('buildings')} ctx={ctx}>{rows => rows.map(b => <Row key={b.id} testId="search-building" onClick={() => nav(`/buildings/${b.id}`)} title={b.name} sub={b.address ?? b.code ?? undefined} />)}</More>
            </Section>
          )}
          {d && (d.events?.length ?? 0) > 0 && show('events') && (
            <Section icon={<CalendarDays className="h-4 w-4" />} title="Events" testId="search-events">
              <More kind="events" first={d.events!} more={more('events')} ctx={ctx}>{rows => rows.map(e => <Row key={e.id} testId="search-event" onClick={() => nav(`/calendar/events/${e.id}`)} title={e.title}
                sub={[DateTime.fromISO(e.starts_local).toFormat(e.all_day ? 'ccc d LLL yyyy' : 'ccc d LLL yyyy, HH:mm'), e.recurring ? 'Repeats' : null, e.location_text].filter(Boolean).join(' · ')} />)}</More>
            </Section>
          )}
          {d && (d.policies?.length ?? 0) > 0 && show('policies') && (
            <Section icon={<ShieldCheck className="h-4 w-4" />} title="Policies" testId="search-policies">
              <More kind="policies" first={d.policies!} more={more('policies')} ctx={ctx}>{rows => rows.map(p => <Row key={p.id} testId="search-policy" onClick={() => nav(`/policies/${p.id}`)} title={p.title}
                sub={p.snippet ? <Headline text={p.snippet} /> : join(p.type_name, `version ${p.version}`)} />)}</More>
            </Section>
          )}
          {(help.length > 0 || (d && d.articles.length > 0)) && show('help') && (
            <Section icon={<CircleHelp className="h-4 w-4" />} title="Help and knowledge base" testId="search-help">
              <More kind="articles" first={d?.articles ?? []} more={more('articles')} ctx={ctx}>{rows => rows.map(a => <Row key={a.id} testId="search-article" onClick={() => nav(`/kb/${a.id}`)} title={a.title} sub={a.snippet ? <Headline text={a.snippet} /> : a.collection_name ?? undefined} />)}</More>
              {help.filter(h => !h.href?.startsWith('/kb/')).map(h => <Row key={`${h.key}#${h.anchor ?? ''}`} testId="search-guide" onClick={() => nav(h.href ?? helpHref(h.key, h.anchor))}
                title={h.title} sub={<Headline text={unescape(h.snippet)} />} />)}
            </Section>
          )}
          {d && d.messages.length > 0 && show('messages') && (
            <section data-testid="search-messages">
              <h2 className="mb-2 flex items-center gap-2 text-sm font-semibold text-slate-500"><MessageSquare className="h-4 w-4" />Messages</h2>
              <More kind="messages" first={d.messages} more={more('messages')} ctx={ctx} inset={false}>{rows => (
                <div className="space-y-2">
                  {rows.map(m => (
                    <div key={m.id} role="button" tabIndex={0} data-testid="search-message" onClick={() => nav(`/c/${m.conversation_id}${m.parent_id ? `?thread=${m.parent_id}` : `#m-${m.id}`}`)}
                      onKeyDown={e => { if (e.key === 'Enter' && e.target === e.currentTarget) nav(`/c/${m.conversation_id}${m.parent_id ? `?thread=${m.parent_id}` : `#m-${m.id}`}`); }} className="block w-full cursor-pointer rounded-lg border border-slate-200 bg-white p-3 text-left hover:border-accent-line dark:border-slate-800 dark:bg-slate-900">
                      <div className="mb-1 flex items-center gap-2 text-xs text-slate-500">
                        {m.conversation_kind === 'channel' ? <Hash className="h-3 w-3" /> : m.conversation_kind === 'room' ? <RoomIcon className="h-3 w-3" /> : <MessageSquare className="h-3 w-3" />}
                        <span>{m.conversation_title ?? 'Direct message'}</span><span>·</span>
                        <PersonButton userId={m.sender_id} name={byId.get(m.sender_id)?.display_name} className="rounded font-medium text-slate-700 hover:underline dark:text-slate-300">{byId.get(m.sender_id)?.display_name}</PersonButton><span>·</span><span>{messageTimestamp(m.created_at)}</span>
                      </div>
                      <div className="text-sm"><Headline text={m.headline || m.body} /></div>
                    </div>
                  ))}
                </div>
              )}</More>
            </section>
          )}
        </div>
      </div>
    </div>
  );
}
